import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Edit3, FileText, Download, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import Forbidden from "@/pages/Forbidden";

const ALLOWED_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant", "manager", "account_manager", "hr"]);
const STATUS_TR = { draft: "Taslak", sent: "Gönderildi", accepted: "Onaylandı", rejected: "Reddedildi", expired: "Süresi Doldu" };
const STATUS_STYLE = {
    draft: "bg-muted text-muted-foreground",
    sent: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
    accepted: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    rejected: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    expired: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
};
const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";
const fmtMoney = (v, c = "TRY") => { try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: c || "TRY", maximumFractionDigits: 2 }).format(v || 0); } catch { return `${v} ${c}`; } };
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; } catch { return iso; } };

function ProposalModal({ open, onClose, onSaved, initial, customers }) {
    const isEdit = Boolean(initial?.id);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        title: "", customer_id: "", currency: "TRY", valid_until: "",
        status: "draft", notes: "", items: [{ description: "", quantity: 1, unit_price: 0, tax_percent: 20 }],
    });
    useEffect(() => {
        if (!open) return;
        if (isEdit) setForm({
            title: initial.title || "", customer_id: initial.customer_id || "",
            currency: initial.currency || "TRY", valid_until: initial.valid_until || "",
            status: initial.status || "draft", notes: initial.notes || "",
            items: initial.items?.length ? initial.items : [{ description: "", quantity: 1, unit_price: 0, tax_percent: 20 }],
        });
        else setForm({
            title: "", customer_id: "", currency: "TRY", valid_until: "",
            status: "draft", notes: "", items: [{ description: "", quantity: 1, unit_price: 0, tax_percent: 20 }],
        });
    }, [open, initial, isEdit]);

    const totals = form.items.reduce((acc, it) => {
        const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
        acc.sub += line; acc.tax += line * ((Number(it.tax_percent) || 0) / 100);
        return acc;
    }, { sub: 0, tax: 0 });
    totals.total = totals.sub + totals.tax;

    const setItem = (i, k, v) => setForm(f => { const c = [...f.items]; c[i] = { ...c[i], [k]: v }; return { ...f, items: c }; });
    const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0, tax_percent: 20 }] }));
    const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Başlık zorunlu.");
        if (!form.customer_id) return toast.error("Müşteri seçin.");
        if (!form.items.length) return toast.error("En az bir kalem ekleyin.");
        setSaving(true);
        try {
            const payload = { ...form, items: form.items.map(x => ({ ...x, quantity: Number(x.quantity) || 0, unit_price: Number(x.unit_price) || 0, tax_percent: Number(x.tax_percent) || 0 })) };
            if (!payload.valid_until) delete payload.valid_until;
            const { data } = isEdit ? await api.put(`/proposals/${initial.id}`, payload) : await api.post("/proposals", payload);
            toast.success(isEdit ? "Teklif güncellendi." : "Teklif oluşturuldu.");
            onSaved && onSaved(data); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isEdit ? "Teklifi Düzenle" : "Yeni Teklif"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input required placeholder="Teklif başlığı" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="proposal-title-input" />
                        <select required className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="proposal-customer-select">
                            <option value="">Müşteri seçin…</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <select className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                            <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option>
                        </select>
                        <input type="date" className={inputCls} value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>

                    <div className="card-elevated p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[13px] font-bold">Kalemler</div>
                            <button type="button" onClick={addItem} className="text-[12px] font-semibold text-[hsl(var(--brand))] hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Kalem Ekle</button>
                        </div>
                        <div className="space-y-2">
                            {form.items.map((it, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <input placeholder="Açıklama" className={`${inputCls} col-span-5`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                                    <input type="number" step="0.01" placeholder="Miktar" className={`${inputCls} col-span-2`} value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                                    <input type="number" step="0.01" placeholder="Birim" className={`${inputCls} col-span-2`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                                    <input type="number" step="0.01" placeholder="KDV %" className={`${inputCls} col-span-2`} value={it.tax_percent} onChange={(e) => setItem(i, "tax_percent", e.target.value)} />
                                    <button type="button" onClick={() => rmItem(i)} className="col-span-1 inline-flex h-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 border-t border-border pt-2 flex justify-end gap-6 text-[12.5px]">
                            <span className="text-muted-foreground">Ara Toplam: <b className="text-foreground tabular-nums">{fmtMoney(totals.sub, form.currency)}</b></span>
                            <span className="text-muted-foreground">KDV: <b className="text-foreground tabular-nums">{fmtMoney(totals.tax, form.currency)}</b></span>
                            <span className="text-[14px] font-bold text-[hsl(var(--brand))] tabular-nums">Toplam: {fmtMoney(totals.total, form.currency)}</span>
                        </div>
                    </div>

                    <textarea rows={2} placeholder="Notlar" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="proposal-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Güncelle" : "Kaydet"}</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function ProposalsPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canWrite, setCanWrite] = useState(false);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [customers, setCustomers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInitial, setModalInitial] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (search) params.search = search;
            if (status) params.status = status;
            const { data } = await api.get("/proposals", { params });
            setRows(data.items || []); setCanWrite(!!data.can_write);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [search, status]);
    useEffect(() => { if (user && ALLOWED_ROLES.has(user.role)) load(); }, [load, user]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/customers", { params: { page_size: 200 } });
                setCustomers((data.items || []).map((c) => ({ id: c.id, name: c.name })));
            } catch (err) { console.debug("silent:", err); }
        })();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Bu teklif silinsin mi?")) return;
        try { await api.delete(`/proposals/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Silindi."); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };
    const downloadPdf = async (row) => {
        try {
            const resp = await api.get(`/proposals/${row.id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = `${row.proposal_number || "teklif"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi.");
        } catch (err) { toast.error(err?.response?.data?.detail || "PDF indirilemedi."); }
    };

    if (!user) return null;
    if (!ALLOWED_ROLES.has(user.role)) return <Forbidden />;

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="proposals-title">Teklifler</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Müşteri bazlı hızlı teklif oluşturma ve PDF çıkış.</p>
                </div>
                {canWrite && <button onClick={() => { setModalInitial(null); setModalOpen(true); }} className="btn-brand h-11" data-testid="btn-new-proposal"><Plus className="h-4 w-4" /> Yeni Teklif</button>}
            </div>

            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input placeholder="Ara..." value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-9`} />
                </div>
                <select className={`${inputCls} max-w-[180px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            <div className="card-elevated overflow-hidden">
                <table className="w-full min-w-[900px]">
                    <thead><tr className="border-b border-border bg-muted/40">
                        {["Teklif No", "Müşteri", "Başlık", "Toplam", "Geçerlilik", "Durum", "İşlem"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[11.5px] font-semibold uppercase text-muted-foreground">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">Yükleniyor…</td></tr>
                        : rows.length === 0 ? <tr><td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">Kayıt yok.</td></tr>
                        : rows.map((r) => (
                            <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/40" data-testid={`proposal-row-${r.id}`}>
                                <td className="px-4 py-3 text-[12px] font-semibold text-muted-foreground">{r.proposal_number || "—"}</td>
                                <td className="px-4 py-3 text-[13px] font-semibold">{r.customer_name || "—"}</td>
                                <td className="px-4 py-3 text-[13px]">{r.title}</td>
                                <td className="px-4 py-3 text-[13px] font-bold tabular-nums">{fmtMoney(r.total, r.currency)}</td>
                                <td className="px-4 py-3 text-[12.5px] text-muted-foreground">{fmtDate(r.valid_until)}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>{STATUS_TR[r.status]}</span></td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-1">
                                        <button onClick={() => downloadPdf(r)} className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-2 py-1 text-[11.5px] font-semibold hover:bg-[hsl(var(--brand))]/20" data-testid={`btn-pdf-${r.id}`}><Download className="h-3 w-3" /> PDF</button>
                                        {canWrite && <>
                                            <button onClick={() => { setModalInitial(r); setModalOpen(true); }} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]"><Edit3 className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => del(r.id)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                                        </>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ProposalModal open={modalOpen} onClose={() => setModalOpen(false)} initial={modalInitial} customers={customers}
                onSaved={(x) => setRows((r) => { const i = r.findIndex(y => y.id === x.id); if (i >= 0) { const c = [...r]; c[i] = x; return c; } return [x, ...r]; })} />
        </div>
    );
}
